import React, { ComponentProps } from 'react';

import { StyleSheet } from 'react-native';
import type { ViewStyle } from 'react-native';

import ActionButton from 'components/action-button';
import { Box, Typography, withTypography } from 'components/ui';
import type { BoxProps } from 'components/ui';
import { primitive, spacing } from 'styles/constants';

import type { QuantityBadgeStyleProps } from './types';

type ImageSize = 'small' | 'medium' | 'large' | 'extraLarge';

const ImageSizeMap: Record<ImageSize, number> = {
  small: 55,
  medium: 60,
  large: 70,
  extraLarge: 80,
};

const normalizeDimension = (value: number | string | undefined) => {
  if (typeof value === 'string' && value.endsWith('px')) {
    const parsedValue = Number(value.slice(0, -2));

    return Number.isNaN(parsedValue) ? value : parsedValue;
  }

  return value;
};

export const ImageContainer = ({ $size, style, ...props }: BoxProps & { $size?: ImageSize }) => {
  const imageSize = $size ? ImageSizeMap[$size] : ImageSizeMap.medium;

  return React.createElement(Box, {
    ...props,
    style: [styles.imageContainer, { height: imageSize, width: imageSize }, style],
  });
};

export const DetailsContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.detailsContainer, style],
  });

export const QuantityBadge = ({
  height,
  justifyContent,
  left,
  style,
  top,
  width,
  ...props
}: QuantityBadgeStyleProps) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.quantityBadge,
      {
        height: normalizeDimension(height),
        justifyContent,
        left: normalizeDimension(left),
        top: normalizeDimension(top),
        width: normalizeDimension(width),
      } as ViewStyle,
      style,
    ],
  });

export const ProductName = ({
  $isProductNameBold,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & { $isProductNameBold?: boolean }) =>
  React.createElement(Typography, {
    variant: $isProductNameBold ? 'headerThree' : 'copyOne',
    m: '$0',
    numberOfLines: 2,
    ...props,
  });

export const ProductDescription = withTypography({ variant: 'copyTwo', m: '$0' });

export const Layout = ({ $inline, style, ...props }: BoxProps & { $inline?: boolean }) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.layout,
      $inline ? styles.layoutInline : styles.layoutStacked,
      $inline ? styles.layoutInlineJustified : styles.layoutStackedJustified,
      style,
    ],
  });

export const SelectionsListItem = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style,
  });

export const SelectionsListItemText = withTypography({
  variant: 'copyTwo',
});

export const actionButtonStyles: ComponentProps<typeof ActionButton> = {
  margin: 0,
  padding: 0,
  alignSelf: 'flex-start',
  _text: {
    textDecorationLine: 'underline',
  },
};

const styles = StyleSheet.create({
  detailsContainer: {
    alignSelf: 'center',
    flex: 1,
    flexShrink: 1,
    justifyContent: 'flex-start',
  },
  imageContainer: {
    justifyContent: 'center',
    margin: spacing.$0,
    marginRight: spacing.$3,
    padding: spacing.$0,
    position: 'relative',
  },
  layout: {
    alignItems: 'flex-start',
    flex: 1,
  },
  layoutInline: {
    flexDirection: 'row',
  },
  layoutInlineJustified: {
    justifyContent: 'flex-start',
  },
  layoutStacked: {
    flexDirection: 'column',
  },
  layoutStackedJustified: {
    justifyContent: 'center',
  },
  quantityBadge: {
    alignItems: 'center',
    backgroundColor: primitive.$bbqBrown,
    borderRadius: 9999,
    flexDirection: 'row',
    height: spacing.$5,
    left: -3,
    position: 'absolute',
    top: -3,
  },
});
